'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdLocalHospital,
    MdClose,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getUnits,
    deleteUnit,
    HospitalUnit,
    UNIT_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function UnitsPage() {
    const navigate = useNavigate()
    const [units, setUnits] = useState<HospitalUnit[]>([])
    const [filteredUnits, setFilteredUnits] = useState<HospitalUnit[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [unitToDelete, setUnitToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        if (selectedHospitalId) {
            loadUnits(selectedHospitalId)
        }
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = units.filter(
                unit =>
                    unit.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    unit.code?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFilteredUnits(filtered)
        } else {
            setFilteredUnits(units)
        }
    }, [searchTerm, units])

    const loadUnits = async (hospitalId?: number) => {
        setLoading(true)
        try {
            const response = await getUnits(undefined, hospitalId)
            if (response.success) {
                setUnits(response.result)
                setFilteredUnits(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar unidades')
            }
        } catch (error) {
            toast.error('Erro ao carregar unidades')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setUnitToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!unitToDelete) return

        setDeleting(true)
        try {
            const response = await deleteUnit(unitToDelete)
            if (response.success) {
                toast.success('Unidade excluída com sucesso')
                loadUnits(selectedHospitalId || undefined)
                setShowDeleteModal(false)
                setUnitToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir unidade')
            }
        } catch (error) {
            toast.error('Erro ao excluir unidade')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando unidades...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Unidades Hospitalares
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {units.length}{' '}
                            {units.length === 1 ? 'unidade' : 'unidades'}{' '}
                            cadastradas
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/unidades/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Adicionar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou código..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredUnits.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdLocalHospital className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma unidade encontrada'
                                : 'Nenhuma unidade cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando unidades hospitalares'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() =>
                                    navigate('/hospital/unidades/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira unidade
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Unidade</th>
                                <th>Tipo</th>
                                <th>Localização</th>
                                <th>Leitos</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUnits.map(unit => (
                                <tr
                                    key={unit.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/unidades/${unit.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdLocalHospital className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <div className="font-bold">
                                                    {unit.name}
                                                </div>
                                                {unit.code && (
                                                    <div className="text-sm text-base-content/60">
                                                        {unit.code}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {UNIT_TYPE_LABELS[unit.type]}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {unit.floor && (
                                                <div>Andar: {unit.floor}</div>
                                            )}
                                            {unit.wing && (
                                                <div>Ala: {unit.wing}</div>
                                            )}
                                            {!unit.floor && !unit.wing && '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="font-medium">
                                            {unit._count?.beds ||
                                                unit.totalBeds ||
                                                0}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${unit.active ? 'badge-success' : 'badge-ghost'}`}
                                        >
                                            {unit.active ? 'Ativa' : 'Inativa'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/hospital/unidades/${unit.id}/editar`
                                                    )
                                                }}
                                                className="btn btn-ghost btn-sm"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleDelete(unit.id)
                                                }}
                                                className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setUnitToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir unidade"
                message="Tem certeza de que deseja excluir esta unidade? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
